import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLang } from "../context/LanguageContext";

export default function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const linkClass = ({ isActive }) =>
    `px-3 py-2 text-sm tracking-wide transition relative ${
      isActive ? "text-amber-200" : "text-white/85 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div
          className={`glass-card flex items-center justify-between px-4 sm:px-6 py-3 transition-all ${
            scrolled ? "shadow-2xl shadow-emerald-950/40" : ""
          }`}
        >
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-600 grid place-items-center shadow-lg shadow-amber-500/30">
              <span className="font-serif text-emerald-950 text-xl font-bold leading-none">O</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif text-white text-lg sm:text-xl tracking-wide">
                L'Oasis
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-200/80">
                Restaurant
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            <NavLink to="/" end className={linkClass} data-testid="nav-home">
              {t.nav_home}
            </NavLink>
            <NavLink to="/restaurant" className={linkClass} data-testid="nav-restaurant">
              {t.nav_restaurant}
            </NavLink>
            <NavLink to="/gourmandise" className={linkClass} data-testid="nav-gourmandise">
              {t.nav_gourmandise}
            </NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="md:hidden glass-pill p-2 text-white"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="md:hidden glass-card mt-2 p-2 flex flex-col"
            data-testid="mobile-nav"
          >
            <NavLink to="/" end className={linkClass} data-testid="mobile-nav-home">
              {t.nav_home}
            </NavLink>
            <NavLink to="/restaurant" className={linkClass} data-testid="mobile-nav-restaurant">
              {t.nav_restaurant}
            </NavLink>
            <NavLink to="/gourmandise" className={linkClass} data-testid="mobile-nav-gourmandise">
              {t.nav_gourmandise}
            </NavLink>
          </div>
        )}
      </div>
    </header>
  );
}
